import React, { useEffect, useState } from 'react';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Calendar as CalendarIcon, List, Edit, Trash2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { CalendarView } from '@/components/CalendarView';
import { SearchableOwnerSelect } from '@/components/SearchableOwnerSelect';

export const Schedules = () => {
  const [schedules, setSchedules] = useState([]);
  const [owners, setOwners] = useState([]);
  const [pets, setPets] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [visitTypes, setVisitTypes] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [convertDialog, setConvertDialog] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingScheduleId, setDeletingScheduleId] = useState(null);
  const [selectedScheduleForConversion, setSelectedScheduleForConversion] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [formData, setFormData] = useState({
    owner_id: '',
    pet_id: '',
    doctor_id: '',
    schedule_datetime: '',
    description: '',
  });
  const [visitFormData, setVisitFormData] = useState({
    visit_type_id: '',
  });

  useEffect(() => {
    fetchSchedules();
    fetchOwners();
    fetchPets();
    fetchDoctors();
    fetchVisitTypes();
  }, []);

  const fetchSchedules = async () => {
    try {
      const response = await api.get('/schedules');
      setSchedules(response.data);
    } catch (error) {
      toast.error('Failed to load schedules');
    }
  };

  const fetchVisitTypes = async () => {
    try {
      const response = await api.get('/visit-types');
      setVisitTypes(response.data);
    } catch (error) {
      console.log('Visit types not yet configured');
    }
  };

  const fetchOwners = async () => {
    try {
      const response = await api.get('/owners');
      setOwners(response.data);
    } catch (error) {
      toast.error('Failed to load owners');
    }
  };

  const fetchPets = async () => {
    try {
      const response = await api.get('/pets');
      setPets(response.data);
    } catch (error) {
      toast.error('Failed to load pets');
    }
  };

  const fetchDoctors = async () => {
    try {
      const response = await api.get('/doctors');
      setDoctors(response.data);
    } catch (error) {
      toast.error('Failed to load doctors');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSchedule) {
        await api.put(`/schedules/${editingSchedule.id}`, formData);
        toast.success('Schedule updated successfully');
      } else {
        await api.post('/schedules', formData);
        toast.success('Schedule created successfully');
      }
      setIsDialogOpen(false);
      setFormData({ owner_id: '', pet_id: '', doctor_id: '', schedule_datetime: '', description: '' });
      setEditingSchedule(null);
      fetchSchedules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      owner_id: schedule.owner_id,
      pet_id: schedule.pet_id,
      doctor_id: schedule.doctor_id || '',
      schedule_datetime: schedule.schedule_datetime,
      description: schedule.description,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id) => {
    setDeletingScheduleId(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/schedules/${deletingScheduleId}`);
      toast.success('Schedule deleted successfully');
      fetchSchedules();
    } catch (error) {
      toast.error('Failed to delete schedule');
    } finally {
      setIsDeleteDialogOpen(false);
      setDeletingScheduleId(null);
    }
  };

  const handleConvertToVisit = async (e) => {
    e.preventDefault();
    if (!selectedScheduleForConversion || !visitFormData.visit_type_id) {
      toast.error('Please select a visit type');
      return;
    }

    try {
      await api.post('/visits', {
        owner_id: selectedScheduleForConversion.owner_id,
        pet_id: selectedScheduleForConversion.pet_id,
        veterinarian_id: selectedScheduleForConversion.doctor_id || '',
        visit_type_id: visitFormData.visit_type_id,
        status: 'Scheduled',
        visit_date: selectedScheduleForConversion.schedule_datetime,
        notes: selectedScheduleForConversion.description
      });
      toast.success('Visit created from schedule successfully');
      setConvertDialog(false);
      setSelectedScheduleForConversion(null);
      setVisitFormData({ visit_type_id: '' });
      fetchSchedules();
    } catch (error) {
      toast.error('Failed to create visit from schedule');
    }
  };

  const openConvertDialog = (schedule) => {
    setSelectedScheduleForConversion(schedule);
    setVisitFormData({ visit_type_id: '' });
    setConvertDialog(true);
  };

  const getOwnerName = (ownerId) => owners.find((o) => o.id === ownerId)?.full_name || 'Unknown';
  const getPetName = (petId) => pets.find((p) => p.id === petId)?.name || 'Unknown';
  const getDoctorName = (doctorId) => doctors.find((d) => d.id === doctorId)?.full_name || 'Not assigned';

  // Filter pets based on selected owner
  const filteredPets = formData.owner_id 
    ? pets.filter((pet) => pet.owner_id === formData.owner_id)
    : [];

  const [activeTab, setActiveTab] = useState('list');

  return (
    <div className="p-6 md:p-12" data-testid="schedules-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-sage-800 mb-2">Schedules</h1>
          <p className="text-gray-600">Manage appointments and schedules</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-sage-500 hover:bg-sage-600 text-white rounded-full shadow-lg"
              onClick={() => {
                setEditingSchedule(null);
                setFormData({ owner_id: '', pet_id: '', doctor_id: '', schedule_datetime: '', description: '' });
              }}
              data-testid="add-schedule-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Schedule
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle>{editingSchedule ? 'Edit Schedule' : 'Add New Schedule'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="schedule-form">
              <div>
                <Label htmlFor="owner_id">Owner</Label>
                <SearchableOwnerSelect
                  owners={owners}
                  value={formData.owner_id}
                  onValueChange={(value) => setFormData({ ...formData, owner_id: value, pet_id: '' })}
                  placeholder="Select owner"
                  testId="schedule-owner-select"
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="pet_id">Pet</Label>
                <Select value={formData.pet_id} onValueChange={(value) => setFormData({ ...formData, pet_id: value })} disabled={!formData.owner_id}>
                  <SelectTrigger className="rounded-xl mt-2" data-testid="schedule-pet-select">
                    <SelectValue placeholder={formData.owner_id ? "Select pet" : "Select owner first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredPets.map((pet) => (
                      <SelectItem key={pet.id} value={pet.id}>
                        {pet.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="doctor_id">Doctor (Optional)</Label>
                <Select value={formData.doctor_id} onValueChange={(value) => setFormData({ ...formData, doctor_id: value })}>
                  <SelectTrigger className="rounded-xl mt-2" data-testid="schedule-doctor-select">
                    <SelectValue placeholder="Select doctor" />
                  </SelectTrigger>
                  <SelectContent>
                    {doctors.map((doctor) => (
                      <SelectItem key={doctor.id} value={doctor.id}>
                        {doctor.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="schedule_datetime">Date & Time</Label>
                <Input
                  id="schedule_datetime"
                  type="datetime-local"
                  value={formData.schedule_datetime}
                  onChange={(e) => setFormData({ ...formData, schedule_datetime: e.target.value })}
                  className="rounded-xl mt-2"
                  required
                  data-testid="schedule-datetime-input"
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="rounded-xl mt-2"
                  required
                  placeholder="Checkup, vaccination, surgery, etc."
                  data-testid="schedule-description-input"
                />
              </div>
              <Button type="submit" className="w-full rounded-full bg-sage-500 hover:bg-sage-600 text-white" data-testid="schedule-submit-button">
                {editingSchedule ? 'Update Schedule' : 'Create Schedule'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full sm:max-w-md grid-cols-2 mb-6">
          <TabsTrigger value="list" className="rounded-full" data-testid="list-view-tab">
            <List className="w-4 h-4 mr-2" />
            List View
          </TabsTrigger>
          <TabsTrigger value="calendar" className="rounded-full" data-testid="calendar-view-tab">
            <CalendarIcon className="w-4 h-4 mr-2" />
            Calendar View
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          <div className="space-y-4">
            {schedules.length === 0 ? (
              <div className="text-center py-12 text-gray-500 bg-white rounded-3xl shadow-lg">
                No schedules found. Add your first schedule to get started!
              </div>
            ) : (
              schedules.map((schedule, idx) => (
                <div
                  key={schedule.id}
                  className="bg-white rounded-3xl shadow-lg p-4 md:p-6 hover:shadow-2xl transition-all duration-300 flex flex-wrap sm:flex-nowrap items-center gap-4 mb-2"
                  data-testid={`schedule-item-${idx}`}
                >
                  <div className="flex-shrink-0 w-16 h-16 bg-sage-500 rounded-xl flex flex-col items-center justify-center text-white">
                    <div className="text-2xl font-bold">
                      {new Date(schedule.schedule_datetime).getDate()}
                    </div>
                    <div className="text-xs">
                      {new Date(schedule.schedule_datetime).toLocaleString('default', { month: 'short' })}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-heading font-bold text-sage-800 mb-1">{schedule.description}</h3>
                    <p className="text-sm text-gray-600">
                      {getPetName(schedule.pet_id)} - {getOwnerName(schedule.owner_id)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {new Date(schedule.schedule_datetime).toLocaleString()} • Dr. {getDoctorName(schedule.doctor_id)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openConvertDialog(schedule)}
                      className="rounded-full bg-green-50 hover:bg-green-100 text-green-700"
                      data-testid={`convert-visit-${idx}`}
                    >
                      <CheckCircle className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(schedule)}
                      className="rounded-full"
                      data-testid={`edit-schedule-${idx}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(schedule.id)}
                      className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-schedule-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </TabsContent>

      </Tabs>

      {activeTab === 'calendar' && (
        <CalendarView
          schedules={schedules}
          owners={owners}
          pets={pets}
          doctors={doctors}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      {/* Convert to Visit Dialog */}
      <Dialog open={convertDialog} onOpenChange={setConvertDialog}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle>Convert Schedule to Visit</DialogTitle>
          </DialogHeader>
          {selectedScheduleForConversion && (
            <form onSubmit={handleConvertToVisit} className="space-y-4">
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  Pet: {getPetName(selectedScheduleForConversion.pet_id)}
                </p>
                <p className="text-sm font-semibold text-gray-700 mb-4">
                  Owner: {getOwnerName(selectedScheduleForConversion.owner_id)}
                </p>
              </div>
              <div>
                <Label htmlFor="visit_type_id">Visit Type</Label>
                <Select value={visitFormData.visit_type_id} onValueChange={(value) => setVisitFormData({ visit_type_id: value })}>
                  <SelectTrigger className="rounded-xl mt-2">
                    <SelectValue placeholder="Select visit type" />
                  </SelectTrigger>
                  <SelectContent>
                    {visitTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full rounded-full bg-sage-500 hover:bg-sage-600 text-white">
                Create Visit
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the schedule.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white rounded-xl">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
